import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, CalculatorIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, TargetIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const BRAND = 'MATIKKA VALMIS™';
export const heroGuarantees = ['Välitön digitaalinen lataus', 'Kertamaksu', 'Ei tilausta', '7 päivän tyytyväisyystakuu'];
export const stats = [{
  value: '240',
  label: 'Sivua pääosissa'
}, {
  value: '6',
  label: 'Digitaalista osaa'
}, {
  value: '9',
  label: 'Luokka-astetta – 1. luokalta 9. luokalle'
}, {
  value: '5',
  label: 'Bonusta mukana'
}, {
  value: '3',
  label: 'Tasoa monisteissa'
}];
export const differenceStats = [{
  value: '1.–9. luokka',
  label: 'Alakoulusta yläkouluun'
}, {
  value: '6 digitaalista osaa',
  label: 'Lukukäsitteestä ja geometriasta algebraan, funktioihin ja arviointiin'
}, {
  value: 'Tulostusvalmiit PDF:t',
  label: 'Käytä digitaalisesti tai tulosta luokkaan'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Illat ja viikonloput katoavat',
  text: 'Tunteja ruudun ääressä ensi viikon matematiikan tuntien suunnittelussa – eikä työ ole vieläkään valmis.'
}, {
  title: 'Tehtävien etsimistä tuntikausia',
  text: 'Eri sivustoilta löytyvät tehtävät ovat liian helppoja, liian vaikeita tai niitä pitää muokata ennen kuin ne toimivat luokassa.'
}, {
  title: 'Suuret tasoerot luokassa',
  text: 'Osa oppilaista tarvitsee välineitä ja tukea, osa on valmis viidessä minuutissa – ja tunnin pitäisi toimia kaikille.'
}, {
  title: 'Oppilaita putoaa kärryiltä',
  text: 'Huomaat, ettei osa ole vielä sisäistänyt kymmenjärjestelmää, murtolukuja tai yhtälöitä – mutta aikaa uuden materiaalin tekemiseen ei ole.'
}, {
  title: 'Arviointi ja dokumentointi ovat työläitä',
  text: 'Opetussuunnitelman tavoitteet, formatiivinen arviointi, arvosanojen kriteerit ja tuen kirjaukset – miten perustelet ja dokumentoit kaiken?'
}, {
  title: 'Stressiä tunnin alkaessa',
  text: 'Kun aika ei riitä, tunti muuttuu helposti muotoon ”tehkää kirjasta tehtävät 1–20” ja viime hetken improvisoinniksi.'
}];
export const afterItems = ['Valmis vuosisuunnitelma 1.–9. luokille', '12 valmista oppituntia minuutti minuutilta', 'Materiaalit ja opettajan ohje jokaiselle tunnille', 'Monisteet kolmella tasolla', 'Vastaukset kaikkiin tehtäviin', 'Linkitetty POPS 2014:n tavoitteisiin', 'Ongelmanratkaisua ja arjen matematiikkaa', 'Kokeet, arviointikriteerit ja valmiit palautekommentit', 'Tulostusvalmiit PDF:t'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Illat kuluvat suunnitteluun',
  text: 'Käytätkö iltoja ja viikonloppuja ensi viikon matematiikan tuntien suunnitteluun?'
}, {
  icon: FolderSearchIcon,
  title: 'Etsit sopivia tehtäviä',
  text: 'Etsitkö jatkuvasti netistä tehtäviä, jotka sopivat luokka-asteelle ja opetussuunnitelman tavoitteisiin?'
}, {
  icon: UsersIcon,
  title: 'Luokassa on suuret tasoerot',
  text: 'Tarvitsetko tehtäviä, jotka toimivat sekä tukea tarvitseville että lisähaastetta kaipaaville?'
}, {
  icon: TargetIcon,
  title: 'Osa oppilaista jää jälkeen',
  text: 'Näetkö oppilaita, joilta puuttuu laskutaidon perusta, mutta aikaa lisämateriaalin tekemiseen ei ole?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Arviointi vie aikaa',
  text: 'Haluaisitko valmiit kokeet, kriteerit ja palautteet – ja tukea päättöarviointiin valmistautumiseen?'
}, {
  icon: LayoutGridIcon,
  title: 'Kaipaat selkeämpää rakennetta',
  text: 'Haluaisitko kaiken yhdessä paikassa, jotta löydät oikean tunnin nopeasti, kun tunti alkaa?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Osa 01',
  level: '1.–2. luokka',
  title: 'Luvut ja laskutoimitukset',
  tagline: 'Tiedät tarkalleen, mitä jokaisella tunnilla tehdään – koko lukuvuoden.',
  intro: 'Ensimmäinen osa antaa sinulle valmiin vuosisuunnitelman 1. ja 2. luokalle sekä kaksitoista valmista oppituntia, jotka voit pitää heti.',
  items: ['Vuosisuunnitelma 1. ja 2. luokalle', '38 kouluviikkoa POPS-tavoitteisiin linkitettynä', '12 valmista oppituntia minuutti minuutilta', '45 minuutin oppitunnit', 'Materiaalit ja opettajan ohje jokaiselle tunnille', 'Lukukäsite 0–100 sekä kympit ja ykköset', 'Yhteen- ja vähennyslasku, kymmenylitys ja kertolaskun alku', 'Lukuparit ja päässälasku', 'Monisteet kolmella tasolla vastauksineen'],
  outro: 'Valmiit tunnit, joiden ansiosta menet luokkaan selkeän suunnitelman kanssa.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Osa 02',
  level: '1.–2. luokka',
  title: 'Geometria, mittaaminen ja tilastot',
  tagline: 'Tee matematiikasta konkreettista ja näkyvää.',
  intro: 'Toinen osa vie oppilaat tasokuvioista ja kappaleista mittaamiseen, kelloon, rahaan ja ensimmäisiin diagrammeihin.',
  items: ['Tasokuviot ja kappaleet', 'Symmetria', 'Pituus, massa, tilavuus ja aika', 'Kello ja raha (eurot ja sentit)', 'Taulukot ja pylväsdiagrammit', 'Ongelmanratkaisu välineiden avulla', 'Monisteet kolmella tasolla', 'Pelit ja vastaukset'],
  outro: 'Toiminnallista matematiikkaa, johon oppilaat pääsevät tarttumaan käsin.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 03',
  level: '3.–6. luokka',
  title: 'Luvut, murtoluvut ja prosentit',
  tagline: 'Vankka lukukäsitys – myös vaikeissa asioissa.',
  intro: 'Kolmas osa näyttää oppilaille, miten suuret luvut, desimaaliluvut, murtoluvut ja prosentit liittyvät toisiinsa.',
  items: ['Suuret luvut ja kymmenjärjestelmä', 'Kertotaulut, allekkain laskeminen ja jakolasku', 'Desimaaliluvut', 'Murtoluvut', 'Prosentit', 'Murtolukujen, desimaalilukujen ja prosenttien yhteys', 'Arviointi ja pyöristäminen', 'Arjen talous', 'Valmiit tunnit, monisteet kolmella tasolla ja vastaukset'],
  outro: 'Selkeät tunnit, jotka tekevät murtoluvuista ja prosenteista ymmärrettäviä kaikille.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Osa 04',
  level: '3.–6. luokka',
  title: 'Algebra, geometria ja todennäköisyys',
  tagline: 'Lukujonoista yhtälöihin, kulmiin ja todennäköisyyteen.',
  intro: 'Neljäs osa tuo mukaan algebrallisen ajattelun ja syventää geometriaa, tilastoja ja ohjelmointia alakoulun ylemmillä luokilla.',
  items: ['Lukujonot ja yhtälöt', 'Lausekkeet', 'Koordinaatisto', 'Kulmat', 'Piiri ja pinta-ala', 'Keskiarvo, mediaani ja tyyppiarvo', 'Todennäköisyys', 'Ohjelmointi ja algoritmit', 'Valmiit tunnit, monisteet ja vastaukset'],
  outro: 'Käytännön tukea abstraktien käsitteiden opettamiseen konkreettisesti.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 05',
  level: '7.–9. luokka',
  title: 'Luvut, algebra ja funktiot',
  tagline: 'Yläkoulun matematiikan ydin – valmiiksi suunniteltuna.',
  intro: 'Viides osa kattaa yläkoulun luvut, algebran ja funktiot – mukana ohjelmointi ja oman talouden hallinta.',
  items: ['Negatiiviset luvut', 'Potenssit ja neliöjuuri', 'Prosenttilaskut ja muutosprosentti', 'Yhtälöt ja epäyhtälöt', 'Lineaarinen funktio', 'Ohjelmointi ja algoritminen ajattelu', 'Talous: budjetti, laina ja korko', 'Monisteet kolmella tasolla ja vastaukset'],
  outro: 'Valmiit tunnit, joiden avulla algebra ja funktiot aukeavat koko luokalle.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 06',
  level: '7.–9. luokka',
  title: 'Geometria, tilastot ja arviointi',
  tagline: 'Näe, mitä oppilaat osaavat – ja valmistele heidät päättöarviointiin.',
  intro: 'Kuudes osa yhdistää geometrian ja tilastot arvioinnin työkaluihin ja 9. luokan päättöarvioinnin valmisteluun.',
  items: ['Pythagoraan lause ja yhdenmuotoisuus', 'Pinta-alat ja tilavuudet', 'Tilastot ja todennäköisyys', 'Formatiivinen arviointi', 'Arviointikriteerit arvosanoille 5, 7 ja 9', 'Kokeet A/B vastauksineen', 'Päättöarvioinnin valmistelu 9. luokalla', '100 valmista palautekommenttia'],
  outro: 'Kattava tuki oikeudenmukaiseen arviointiin ja oppilaiden valmistamiseen.',
  note: 'Materiaali on itsenäisesti laadittu, eikä se ole Opetushallituksen virallinen julkaisu.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitaalista osaa matematiikkaan',
  text: 'Kokonainen opetusjärjestelmä, joka auttaa suunnittelemaan ja toteuttamaan matematiikan opetuksen koko lukuvuoden ajan. Jokaisessa osassa on 40 sivua valmiita oppitunteja, monisteita kolmella tasolla ja vastaukset.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Valmiit tunnit minuutti minuutilta',
  text: 'Ei enää tyhjää paperia. Selkeät tuntisuunnitelmat aikatauluineen, materiaaleineen ja opettajan ohjeineen auttavat pääsemään nopeasti alkuun.'
}, {
  icon: FileTextIcon,
  title: 'Monisteet kolmella tasolla',
  text: 'Sama tavoite kolmella tasolla – tuki, perus ja haaste – jotta kaikki oppilaat voivat työskennellä saman asian parissa.'
}, {
  icon: CalculatorIcon,
  title: 'Vastaukset kaikkiin tehtäviin',
  text: 'Vastaukset monisteisiin, tehtäviin ja kokeisiin, joten säästät aikaa tarkistamisessa.'
}, {
  icon: LayersIcon,
  title: 'POPS 2014:n mukainen',
  text: 'Matematiikan tavoitteet ja sisältöalueet vuosiluokille 1–2, 3–6 ja 7–9 – selkeästi linkitettyinä tunteihin.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Arviointi',
  text: 'Arviointikriteerit, kokeet A/B, päättöarvioinnin valmistelu ja 100 valmista palautekommenttia.'
}, {
  icon: CalendarCheckIcon,
  title: 'Vuosisuunnitelma',
  text: 'Tukea koko lukuvuoden jäsentämiseen ja aiheiden valintaan luokka-asteen ja ryhmän mukaan.'
}];
// Real pages from Osa 1 (Gamma Estudio; WebP 1512x1890 in Impultienda Recursos, store 8b8fa9d9-...).
const PAGE_BASE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/8b8fa9d9-995e-4832-85d2-19be84443c33/';
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: `${PAGE_BASE}1792621922123_mv-osa1-sivu01-08cf03a6.webp`,
  label: 'Osa 1 · Kansilehti',
  title: 'Luvut ja laskutoimitukset · 1.–2. luokka',
  text: 'Jokainen osa alkaa selkeällä katsauksella aiheisiin ja sisältöön.'
}, {
  src: `${PAGE_BASE}1792621927400_mv-osa1-sivu12-a1ec9226.webp`,
  label: 'Osa 1 · Sivu 12',
  title: 'Oppitunti 1: Luvut 0–10 – lukumäärä ja numero',
  text: 'Valmis 45 minuutin tunti: tavoite, materiaalit, kulku minuutti minuutilta ja opettajan ohje.'
}, {
  src: `${PAGE_BASE}1792621932384_mv-osa1-sivu13-3cc52e18.webp`,
  label: 'Osa 1 · Sivu 13',
  title: 'Moniste: Luvut 0–10',
  text: 'Sama tavoite kolmella tasolla – tuki, perus ja haaste – ja vastaukset opettajalle.'
}, {
  src: `${PAGE_BASE}1792621937960_mv-osa1-sivu31-05b5b861.webp`,
  label: 'Osa 1 · Sivu 31',
  title: 'Oppitunti 10: Moniste – laskuperheet',
  text: 'Laskuperheet kolmella tasolla, kuvallinen malli ja vastaukset samalla sivulla.'
}, {
  src: `${PAGE_BASE}1792621942946_mv-osa1-sivu36-a5b6880f.webp`,
  label: 'Osa 1 · Sivu 36',
  title: 'Koe · 1. luokka',
  text: 'Valmis koe kymmenellä tehtävällä ja lukusuoralla – tulosta ja käytä heti.'
}];
export const pageTypes = ['Valmiit oppitunnit', 'Monisteet kolmella tasolla', 'Vastaukset', 'Arviointi'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vuosisuunnitelma matematiikkaan – viikkosuunnitelmat 1.–9. luokille',
  text: 'Valmiit viikkosuunnitelmat koko lukuvuodelle, joten tiedät aina, mitä ensi viikolla tehdään.',
  items: ['Viikkosuunnitelmat 1.–9. luokille', '38 kouluviikkoa', 'Linkitetty POPS-tavoitteisiin', 'Tulostusvalmis'],
  benefit: 'Koko vuosi valmiina – jo ennen kuin se alkaa.'
}, {
  title: '100 lisämonistetta matematiikkaan – vastauksineen',
  text: 'Lisäharjoitusta oppilaille, jotka tarvitsevat enemmän, jotka ovat valmiita ennen muita – tai kotitehtäviksi.',
  items: ['100 monistetta', 'Kaikille luokka-asteille', 'Eri vaikeustasoilla', 'Vastaukset mukana'],
  benefit: 'Aina jotain mielekästä annettavaa oppilaille.'
}, {
  title: 'Matematiikkapelit – 40 peliä ilman valmistelua',
  text: 'Pelejä, jotka tekevät harjoittelusta ja kertaamisesta aktiivista ja hauskaa. Säännöt on kirjoitettu selkeästi auki.',
  items: ['40 peliä', 'Ei valmistelua', 'Koko luokalle, pareittain ja ryhmissä', 'Päässälaskua ja lukukäsitettä'],
  benefit: 'Enemmän motivaatiota – vähemmän jankutusta.'
}, {
  title: '5 minuutin päässälaskutuokiot – 180 lämmittelyä koko lukuvuodelle',
  text: 'Lyhyet päässälaskutuokiot tunnin alkuun: oppilaat virittäytyvät matematiikkaan heti ensimmäisestä minuutista.',
  items: ['180 lämmittelyä', 'Riittää koko lukuvuodeksi', '5 minuuttia kerrallaan', 'Vastaukset mukana'],
  benefit: 'Jokainen tunti alkaa rauhallisesti ja keskittyneesti.'
}, {
  title: 'Sijaispaketti – 12 valmista matematiikan tuntia',
  text: 'Kaksitoista valmista tuntia, jotka sijainen voi pitää ilman valmistelua.',
  items: ['12 valmista tuntia', 'Ohjeet sijaiselle', 'Monisteet vastauksineen', 'Tulostusvalmis'],
  benefit: 'Ei hukattuja tunteja, kun olet poissa.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Avaa MATIKKA VALMIS™',
  text: 'Lataa paketti ja avaa luokka-asteellesi sopiva osa.'
}, {
  title: 'Valitse luokka ja tunti',
  text: 'Löydä ryhmällesi sopiva tunti, aihe tai moniste.'
}, {
  title: 'Valmistaudu 5 minuutissa',
  text: 'Lue opettajan ohje ja ota tarvittavat materiaalit esille.'
}, {
  title: 'Tulosta ja aloita tunti',
  text: 'Tulosta monisteet tai käytä materiaalia digitaalisesti – ja mene tunnille selkeän suunnitelman kanssa.'
}];
export const audienceItems = ['Opetat matematiikkaa 1.–9. luokalla', 'Olet luokanopettaja ja lukujärjestyksessäsi on matematiikkaa', 'Olet erityisopettaja ja tuet oppilaita matematiikassa', 'Haluat valmiin vuosisuunnitelman POPS 2014:n mukaan', 'Opetat ryhmiä, joissa on suuret tasoerot', 'Haluat monisteet kolmella tasolla vastauksineen', 'Haluat tukea arviointiin ja päättöarviointiin', 'Haluat säästää iltasi ja viikonloppusi'];
export const timeValueTasks = ['Tehtävien etsiminen netistä', 'Tuntien suunnittelu', 'Monisteiden tekeminen eri tasoille', 'Vastausten ja kokeiden laatiminen', 'Arviointi ja palautteen antaminen'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitaalista osaa – 240 sivua',
  detail: 'Luvut ja laskutoimitukset, geometria ja mittaaminen, murtoluvut ja prosentit, algebra, funktiot ja arviointi'
}, {
  label: '5 bonusta ilman lisämaksua',
  detail: 'Vuosisuunnitelma, 100 lisämonistetta, Matematiikkapelit, 5 minuutin päässälaskutuokiot ja Sijaispaketti'
}, {
  label: '1.–9. luokille',
  detail: 'Valmiit oppitunnit, monisteet kolmella tasolla, vastaukset ja arviointikriteerit'
}];
export const offerIncludes = ['Välitön digitaalinen lataus', 'Tallennettavat PDF-tiedostot', 'Tulostusvalmis', 'Fyysistä tuotetta ei lähetetä'];
export const PRICE = '25 €';
export const PRICE_BEFORE = '55 €';

// Only add authentic reviews of MATIKKA VALMIS. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Miten saan materiaalin?',
  a: 'MATIKKA VALMIS™ toimitetaan digitaalisesti. Oston jälkeen saat käyttöösi PDF-tiedostot, jotka voit ladata ja tallentaa. Voit käyttää materiaalia tietokoneella, tabletilla tai puhelimella ja tulostaa tarvitsemasi sivut.'
}, {
  q: 'Onko materiaali POPS 2014:n mukainen?',
  a: 'Kyllä. Vuosisuunnitelma ja oppitunnit on linkitetty perusopetuksen opetussuunnitelman perusteiden (POPS 2014) matematiikan tavoitteisiin ja sisältöalueisiin vuosiluokille 1–2, 3–6 ja 7–9. MATIKKA VALMIS™ on itsenäisesti laadittu opetusmateriaali, eikä se ole Opetushallituksen virallinen julkaisu.'
}, {
  q: 'Mille luokille materiaali sopii?',
  a: 'Materiaali kattaa luokat 1–9: osat 1 ja 2 ovat 1.–2. luokille, osat 3 ja 4 ovat 3.–6. luokille ja osat 5 ja 6 ovat 7.–9. luokille. Valitset tunnit ja tehtävät oman luokkasi ja ryhmäsi mukaan.'
}, {
  q: 'Voinko todella valmistella tunnin 5 minuutissa?',
  a: 'MATIKKA VALMIS™ on tehty nopeuttamaan valmistelua. Avaat osan, valitset valmiin tunnin ja luet sen läpi noin viidessä minuutissa. Aika voi vaihdella tunnin ja eriyttämisen tarpeen mukaan.'
}, {
  q: 'Riittääkö materiaali koko lukuvuodeksi?',
  a: 'MATIKKA VALMIS™ sisältää vuosisuunnitelman 38 kouluviikolle sekä valmiit tunnit ja kokonaisuudet kaikissa osissa. Valitset, yhdistelet ja muokkaat materiaalia oman suunnitelmasi ja oppilaidesi tarpeiden mukaan.'
}, {
  q: 'Onko monisteita eri tasoilla?',
  a: 'Kyllä. Monisteet ovat kolmella tasolla – tuki, perus ja haaste – joten eri tarpeisiin vastaavat oppilaat voivat työskennellä saman tavoitteen parissa.'
}, {
  q: 'Onko mukana vastaukset?',
  a: 'Kyllä. Monisteisiin, tehtäviin ja kokeisiin on vastaukset, joten säästät aikaa tarkistamisessa.'
}, {
  q: 'Sisältyykö arviointi?',
  a: 'Kyllä. Osa 6 sisältää formatiivisen arvioinnin työkalut, arviointikriteerit arvosanoille 5, 7 ja 9, kokeet A/B vastauksineen, päättöarvioinnin valmistelun 9. luokalla ja 100 valmista palautekommenttia.'
}, {
  q: 'Auttaako materiaali oppimisen tuen uudistuksessa?',
  a: 'Oppimisen tuen uudistus tuli voimaan 1.8.2025. Monisteet kolmella tasolla ja selkeät tavoitteet helpottavat eriyttämistä ja tukiopetuksen suunnittelua. Materiaali ei korvaa koulusi omia tukikäytäntöjä eikä pedagogista dokumentointia.'
}, {
  q: 'Pitääkö kaikki tulostaa?',
  a: 'Ei. Valitset itse, mitkä sivut tulostat, ja voit käyttää osaa materiaalista myös digitaalisesti.'
}, {
  q: 'Sisältyvätkö viisi bonusta hintaan?',
  a: `Kyllä. Kaikki viisi bonusta sisältyvät pakettiin ilman lisämaksua – kaikki yhteensä ${PRICE}.`
}, {
  q: 'Onko kyseessä tilaus?',
  a: 'Ei. Maksat kerran ja saat digitaalisen materiaalin käyttöösi. Ei toistuvia veloituksia.'
}, {
  q: 'Voinko käyttää materiaalia puhelimella tai tabletilla?',
  a: 'Kyllä. PDF-tiedostot aukeavat tietokoneella, tabletilla ja puhelimella. Monisteiden tarkasteluun ennen tulostusta suosittelemme tietokonetta tai tablettia.'
}, {
  q: 'Onko tuotteella takuu?',
  a: 'Kyllä. MATIKKA VALMIS™ -paketilla on 7 päivän tyytyväisyystakuu. Jos materiaali ei vastaa odotuksiasi, voit ottaa meihin yhteyttä ja pyytää rahat takaisin takuuajan sisällä ehtojen mukaisesti.'
}];
export const footerLinks = ['Ostoehdot', 'Tietosuoja', 'Palautukset', 'Yhteystiedot'];
